import { ChangeDetectorRef, Directive, EventEmitter, Input, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ApiService } from '@quorum/api';
import { AuthenticationStateService } from '@quorum/authentication/services';
import {
  Employee,
  TechnicianGroup,
  ServiceAppointment as Appointment,
  ServiceCode,
  TransportOption,
  Vehicle,
} from '@quorum/models/xs-resource';
import { SystemControlStateService } from '@quorum/xsr-system-control/services';
import { createFormGroupFromObject } from 'libs/sha-services/src/lib/createFormGroupFromObject';
import { SystemControlState } from 'libs/xsr-system-control/src/lib/+state/system-control.interfaces';
import * as moment from 'moment';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { filter, map, startWith, take } from 'rxjs/operators';

@Directive()
export class ServiceAppointmentBaseComponent {
  @Input() appointment$: Observable<Appointment>;
  @Input() vehicle: Vehicle;
  @Input() appointmentDropdowns$: Observable<any>;
  @Output() appointmentFormEmit: EventEmitter<FormGroup> = new EventEmitter();
  @Output() appointmentFormChanged: EventEmitter<boolean> = new EventEmitter();
  appointmentForm: FormGroup;

  systemControl$: Observable<SystemControlState>;
  authenticatedEmployee$: Observable<Employee>;
  phoneNumberMask = ['(', /[1-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/];
  currentEmployee: Employee;
  cadillacConcierge: boolean = false;
  techOrTechGroupSub: Subscription;
  odometerInSub: Subscription;
  appointmentFormSub: Subscription;
  sameAsPickupSub: Subscription;

  maxDate: Date = new Date(new Date().setFullYear(new Date().getFullYear() + 5));
  constructor(
    public systemControlStateService: SystemControlStateService,
    public authenticationStateService: AuthenticationStateService,
    public apiService: ApiService,
    public cdr: ChangeDetectorRef
  ) {}

  Init() {
    this.authenticationStateService
      .selectAuthenticatedEmployee()
      .pipe(
        filter((currentUser) => currentUser != null),
        take(1),
        map((currentUser) => {
          this.currentEmployee = currentUser;
          return currentUser.rooftopId;
        })
      )
      .subscribe((rooftopId) => {
        combineLatest([
          this.systemControlStateService.getSystemControlValues(
            [
              // 'CL_GM_DASHBOARD_INTEGRATION_ENABLED', - blows up if not GM
              'CL_ENABLE_MAINTENANCE_MENU',
              'CL_SERVICE_CODE_REQUIRED',
              'CL_SERV_APPT_ASSIGN_TECH_GRP_NAME',
              'CL_COMMUNICATOR_ENABLE',
              'CL_CURRENCY',
              'CL_SHOP_SUPPLIES_PERCENT',
              'CL_UNIT_OF_MEASURE',
              'CL_ELECTRONIC_WORK_ORDER_FLAG',
              'CL_CITY',
              'CL_WORK_RECON_LINK_REF',
              'CL_GLOBAL_WARRANTY',
            ],
            rooftopId
          ),
          this.appointment$.pipe(filter((appointment) => appointment != null)),
          this.authenticationStateService
            .selectAuthenticatedEmployee()
            .pipe(filter((authenticatedEmployee) => authenticatedEmployee != null)),
        ])
          .pipe(take(1))
          .subscribe(([systemControls, appointment, authenticatedEmployee]) => {
            // if (appointment.serviceAdvisorId === null) {
            //   appointment = { ...appointment, serviceAdvisorId: authenticatedEmployee.associateId };
            // }
            this.appointmentForm = createFormGroupFromObject(appointment);
            this.appointmentForm.addControl(
              'appointmentTime',
              new FormControl(
                appointment.dateTime ? new Date(appointment.dateTime).toTimeString().substring(0, 5) : null
              )
            );
            this.appointmentForm.addControl(
              'promisedTime',
              new FormControl(
                appointment.promisedDateTime
                  ? new Date(appointment.promisedDateTime).toTimeString().substring(0, 5)
                  : 'noPromisedTime'
              )
            );
            this.appointmentForm.addControl(
              'techOrTechGroup',
              new FormControl(appointment.technicianGroupId ? appointment.technicianGroupId : appointment.technicianId)
            );

            if (
              systemControls['CL_SERVICE_CODE_REQUIRED'] &&
              systemControls['CL_SERVICE_CODE_REQUIRED'].value === 'H'
            ) {
              this.appointmentForm.get('serviceCode').setValidators(Validators.required);
            }
            // if (systemControls['CL_WO_AUTO_TAG'].value === 'Y') {
            //   this.appointmentForm.get('tagNumber').disable();
            // }
            // if (systemControls['CL_TAG_NUM_REQUIRED'].value === '1') {
            //   this.appointmentForm.get('tagNumber').setValidators(Validators.required);
            // }

            this.appointmentForm.get('dateTime').setValidators(Validators.required);
            this.appointmentForm.get('appointmentTime').setValidators(Validators.required);
            this.appointmentForm.get('odometerIn').setValidators([Validators.pattern('^[0-9]+$')]);
            this.appointmentForm.get('employeeId').setValidators(Validators.required);
            this.appointmentForm.get('serviceAdvisorId').setValidators(Validators.required);
            this.appointmentForm.get('priorityNumber').setValidators([Validators.pattern('^[0-9]+$')]);
            // Validators added after the group was built are inert until each control re-evaluates;
            // a group-level updateValueAndValidity() only re-reads the children's cached status.
            Object.values(this.appointmentForm.controls).forEach((control) =>
              control.updateValueAndValidity({ emitEvent: false })
            );
            this.appointmentForm.get('department').setValue(appointment.department ? appointment.department : 'M');
            this.appointmentDropdowns$.pipe(take(1)).subscribe((appointmentDropdowns) => {
              let serviceCode: ServiceCode = appointmentDropdowns.serviceCodes.find(
                (s: any) => s.serviceCode == this.appointmentForm.get('serviceCode').value
              );
              this.appointmentForm
                .get('priorityNumber')
                .setValue(appointment.priorityNumber ? appointment.priorityNumber : serviceCode?.priorityNumber);

              let promisedTime: string = '';
              if (this.appointmentForm.get('promisedTime').value) {
                promisedTime = appointment.promisedDateTime
                  ? moment(appointment.promisedDateTime.toString().split('T')[1].substr(0, 5), 'hh:mm')
                      .subtract(new Date().getTimezoneOffset(), 'minutes')
                      .format('HH:mm')
                  : null;
              } else {
                promisedTime = serviceCode?.dateTimePromised
                  ? moment(serviceCode.dateTimePromised.split('T')[1].substr(0, 5), 'hh:mm')
                      .subtract(new Date().getTimezoneOffset(), 'minutes')
                      .format('HH:mm')
                  : null;
              }
              this.appointmentForm.get('promisedTime').setValue(promisedTime);
              this.mapDateTimes('promisedDateTime', promisedTime);
            });
            this.appointmentDropdowns$
              .pipe(
                filter((dropdowns) => dropdowns != null && dropdowns['transportOptions']),
                map((dropdowns) => dropdowns.transportOptions),
                take(1)
              )
              .subscribe((transportOptions) => {
                let appointmentTransportOption = transportOptions.find(
                  (transportOption: TransportOption) =>
                    transportOption.id === this.appointmentForm.get('transportOptionId').value
                );
                if (
                  appointmentTransportOption &&
                  appointmentTransportOption.name.toLowerCase() === 'cadillac premium concierge'
                ) {
                  this.cadillacConcierge = true;
                } else {
                  this.cadillacConcierge = false;
                }
              });

            this.techOrTechGroupSub = this.appointmentForm
              .get('techOrTechGroup')
              .valueChanges.subscribe((value: any) => {
                if (value.type == 'group') {
                  this.appointmentForm.get('technicianGroupId').setValue(value.id);
                  this.appointmentForm.get('technicianGroupName').setValue(value.name);
                } else {
                  this.appointmentForm.get('technicianId').setValue(value.id);
                  this.appointmentForm.get('technicianGroupName').setValue(value.name);
                }
              });
            this.odometerInSub = this.appointmentForm.get('odometerIn').valueChanges.subscribe((value: any) => {
              if (value && value !== '' && value < this.vehicle.currentMileage) {
                this.appointmentForm.get('odometerIn').setErrors({ invalidOdometer: this.vehicle.currentMileage });
              } else {
                this.appointmentForm.get('odometerIn').setErrors(null);
                this.appointmentForm.get('odometerIn').updateValueAndValidity({ emitEvent: false });
              }
            });
            this.sameAsPickupSub = this.appointmentForm
              .get('cadillacConciergeUsePickupLocation')
              .valueChanges.subscribe((value) => {
                if (value) {
                  this.appointmentForm.get('cadillacConciergeDropoff').disable();
                  this.appointmentForm
                    .get('cadillacConciergeDropoff')
                    .setValue(this.appointmentForm.get('cadillacConciergePickup').value);
                } else {
                  this.appointmentForm.get('cadillacConciergeDropoff').enable();
                }
              });
            this.appointmentForm.markAllAsTouched();
            this.appointmentFormSub = this.appointmentForm.valueChanges
              .pipe(startWith(this.appointmentForm))
              .subscribe((value) => {
                if (this.appointmentForm.dirty) {
                  this.appointmentFormChanged.emit(true);
                }
                this.appointmentFormEmit.emit(this.appointmentForm);
              });
          });
      });
  }

  mapDateTimes(fieldName: string, time: string) {
    let timeDate = moment(time, 'hh:mm').toDate();
    if (time) {
      let dankDate = this.appointmentForm.get(fieldName).value
        ? new Date(this.appointmentForm.get('dateTime').value)
        : new Date();
      dankDate.setHours(timeDate.getHours());
      dankDate.setMinutes(timeDate.getMinutes());
      this.appointmentForm.get(fieldName).setValue(dankDate);
    }
  }

  techOrGroupSelected(event: any, technicians: Employee[], techGroups: TechnicianGroup[]) {
    if (technicians.findIndex((t) => t.associateId == event.value) > -1) {
      let tech: Employee = technicians.find((t) => t.associateId == event.value);
      this.appointmentForm.get('technicianId').setValue(tech.associateId);
      this.appointmentForm
        .get('technicianGroupName')
        .setValue(`${tech.embedded.associate.firstName} ${tech.embedded.associate.lastName} `);
      this.appointmentForm.get('technicianGroupId').setValue(null);
    } else {
      let group: TechnicianGroup = techGroups.find((g) => g.id == event.value);
      this.appointmentForm.get('technicianGroupId').setValue(group.id);
      this.appointmentForm.get('technicianGroupName').setValue(group.description);
      this.appointmentForm.get('technicianId').setValue(null);
    }
  }
  transportOptionSelected(event: any, transportOptions: TransportOption[]) {
    let selectedTransportOption = transportOptions.find((transportOption) => transportOption.id === event);
    if (selectedTransportOption.name.toLowerCase() === 'cadillac premium concierge') {
      this.cadillacConcierge = true;
      this.appointmentForm.get('cadillacConciergeContactPhone').setValidators(Validators.required);
      this.appointmentForm.get('cadillacConciergeContactPhone').updateValueAndValidity();
    } else {
      this.cadillacConcierge = false;
      this.appointmentForm.get('cadillacConciergeContactPhone').clearValidators();
      this.appointmentForm.get('cadillacConciergeContactPhone').updateValueAndValidity();
      this.appointmentForm.patchValue({
        cadillacConciergeContactPhone: null,
        cadillacConciergeDropoff: null,
        cadillacConciergePickup: null,
        cadillacConciergeUsePickupLocation: null,
      });
    }
  }
  serviceCodeSelected(serviceCode: string, serviceCodes: ServiceCode[]) {
    let sc = serviceCodes.find((s) => s.serviceCode == serviceCode);
    let timeDate = null;

    if (sc.dateTimePromised != null && sc.activeFlag == '1') {
      timeDate = moment(sc.dateTimePromised.split('T')[1].substr(0, 5), 'hh:mm')
        .subtract(new Date().getTimezoneOffset(), 'minutes')
        .format('HH:mm');
    }
    this.appointmentForm.patchValue({ priorityNumber: sc.priorityNumber, promisedTime: timeDate });
  }
}
